// Client-safe HeyGen v3 avatar-quality config. NO 'server-only', NO db
// imports — this is imported by the client-side quality picker as well as
// server-side resolvers/helpers. See `apps/dashboard/lib/avatars/resolve-avatar-engine.ts`
// for the richer (server-only-adjacent but still pure) engine resolver that
// builds on top of this file.

// Client-safe too (pure data, no keys()/db).
import { APPSUMO_AVATAR_CREDITS_PER_MINUTE } from './appsumo/tier-limits';

/**
 * The three user-facing quality tiers, mirroring the ElevenLabs voice
 * quality selector: the user picks a quality and the HeyGen engine follows
 * 1:1 (`TIER_ENGINE`). Unlike engine selection, the credit multiplier is
 * NOT 1:1 with the tier — HeyGen bills by avatar type x engine (see
 * `HEYGEN_COST_PER_MIN_USD` below), so the same tier burns different rates
 * depending on whether the selected look is a studio avatar, a photo
 * avatar, or a digital twin. `resolveAvatarBurnMultiplier` is the single
 * place that turns (engine, avatarType) into the burn rate.
 *
 *   NORMAL   -> avatar_iii (1x studio/twin, 2.6x photo)
 *   STANDARD -> avatar_iv  (3x photo, 4x studio/twin)
 *   HIGH     -> avatar_v   (4x, any type) — default
 */
export type AvatarQualityTier = 'NORMAL' | 'STANDARD' | 'HIGH';
export type AvatarEngine = 'avatar_iii' | 'avatar_iv' | 'avatar_v';

/**
 * Which rate card an org burns avatar minutes on. `'grid'` is HeyGen's
 * engine x avatar-type grid below (every Stripe org). `'flat'` is the
 * contractual AppSumo rate — 1/3/4 credits per minute by quality tier,
 * independent of engine and avatar type.
 *
 * This file is client-safe and cannot read a plan source, so `pricing` is
 * threaded in explicitly by the caller (server code resolves it with
 * `getPlanSource`; UI carries it on the avatar quota DTO). It is REQUIRED on
 * every billing-path function so a new charge site cannot silently default an
 * AppSumo org onto the grid; only the display-only helpers default it.
 */
export type AvatarPricingMode = 'grid' | 'flat';

export const AVATAR_QUALITY_TIERS: readonly AvatarQualityTier[] = [
  'NORMAL',
  'STANDARD',
  'HIGH'
];

/**
 * The tier every avatar surface seeds itself with. Product decision: avatar
 * clips are the showcase moment of a video, so the picker leads with the best
 * result rather than the cheapest — HIGH (avatar_v) is the default everywhere
 * a tier isn't already persisted. Note the cost implication: a photo look now
 * bills 4x/min at the default instead of the 3x it burned under STANDARD.
 * Looks that can't render avatar_v are still coerced down by
 * `coerceTierForLook`, so this is a preference, not a guarantee.
 */
export const DEFAULT_AVATAR_QUALITY_TIER: AvatarQualityTier = 'HIGH';

/** Tier -> the HeyGen v3 engine that renders it. */
export const TIER_ENGINE: Record<AvatarQualityTier, AvatarEngine> = {
  NORMAL: 'avatar_iii',
  STANDARD: 'avatar_iv',
  HIGH: 'avatar_v'
};

/**
 * Engine -> the tier it renders. Inverse of `TIER_ENGINE`, used to keep the
 * persisted job snapshot's (qualityTier, engine, burnMultiplier) triple
 * internally consistent when a requested tier had to fall back to another
 * engine (unsupported look, clip over the Avatar V cap).
 */
export const ENGINE_TIER: Record<AvatarEngine, AvatarQualityTier> = {
  avatar_iii: 'NORMAL',
  avatar_iv: 'STANDARD',
  avatar_v: 'HIGH'
};

// HeyGen's own avatarType classification, mirrored here (not imported from
// @workspace/database/schema, which this file must stay free of) as the
// lookup key for the pricing grid below.
export type HeygenAvatarTypeForPricing =
  | 'studio_avatar'
  | 'photo_avatar'
  | 'digital_twin';

/**
 * HeyGen's official USD/min pricing grid (developers.heygen.com/docs/pricing,
 * Aug 2026) — the single source of truth for both the admin COGS estimate
 * (`estimateClipCostUsd` in `apps/dashboard/lib/avatars/heygen-pricing.ts`)
 * AND the org-billing burn multiplier (`resolveAvatarBurnMultiplier` below).
 *
 * Source figures: avatar_iii studio/twin $0.0167/s (~$1/min), avatar_iii
 * photo $0.0433/s (~$2.6/min), avatar_iv photo $0.05/s (~$3/min), avatar_iv
 * studio/twin $0.0667/s (~$4/min), avatar_v $0.0667/s (~$4/min, any type).
 *
 * NOTE: HeyGen does NOT actually sell avatar_v for studio/photo (Avatar V is
 * digital-twin-only per developers.heygen.com/models) — those cells exist
 * only to keep the record total so lookups can't miss; resolveAvatarEngine
 * dispatches avatar_v only with engine evidence from the synced catalog,
 * keeping them unreachable in practice.
 */
export const HEYGEN_COST_PER_MIN_USD: Record<
  AvatarEngine,
  Record<HeygenAvatarTypeForPricing, number>
> = {
  avatar_iii: {
    studio_avatar: 1.0,
    photo_avatar: 2.6,
    digital_twin: 1.0
  },
  avatar_iv: {
    photo_avatar: 3.0,
    studio_avatar: 4.0,
    digital_twin: 4.0
  },
  avatar_v: {
    studio_avatar: 4.0,
    photo_avatar: 4.0,
    digital_twin: 4.0
  }
};

/**
 * Baseline rate ($1.00/min — studio/twin @ avatar_iii) that burn multipliers
 * are expressed relative to: a multiplier of Nx means "N times what a
 * baseline clip costs".
 */
const HEYGEN_BASELINE_COST_PER_MIN_USD =
  HEYGEN_COST_PER_MIN_USD.avatar_iii.studio_avatar;

/** USD/min -> burn multiplier, relative to the baseline. */
const toMultiplier = (usdPerMinute: number): number =>
  usdPerMinute / HEYGEN_BASELINE_COST_PER_MIN_USD;

function rangeForEngine(engine: AvatarEngine): { min: number; max: number } {
  const rates = Object.values(HEYGEN_COST_PER_MIN_USD[engine]).map(
    toMultiplier
  );
  return { min: Math.min(...rates), max: Math.max(...rates) };
}

/**
 * The spread of burn rates each tier can produce across HeyGen's avatar
 * types, derived once from the USD grid at load: NORMAL 1–2.6x, STANDARD
 * 3–4x, HIGH 4x. `.max` doubles as the unknown-type billing fallback.
 */
const TIER_CREDITS_RANGE: Record<
  AvatarQualityTier,
  { min: number; max: number }
> = {
  NORMAL: rangeForEngine('avatar_iii'),
  STANDARD: rangeForEngine('avatar_iv'),
  HIGH: rangeForEngine('avatar_v')
};

/**
 * Normalize a raw/possibly-unsynced avatarType string to the pricing grid's
 * key type. Returns null for unknown/null values (never-synced looks,
 * historic rows predating this column) — unlike the COGS estimator's
 * `normalize(...) ?? 'photo_avatar'` fallback in
 * `apps/dashboard/lib/avatars/heygen-pricing.ts`, billing must never
 * silently assume photo, since that can under-bill a studio avatar or twin.
 */
export function normalizeAvatarTypeForPricing(
  avatarType?: string | null
): HeygenAvatarTypeForPricing | null {
  if (
    avatarType === 'studio_avatar' ||
    avatarType === 'photo_avatar' ||
    avatarType === 'digital_twin'
  ) {
    return avatarType;
  }
  return null;
}

/**
 * (engine, avatarType) -> avatar-minute burn multiplier, derived from the USD
 * grid above (multiplier = HEYGEN_COST_PER_MIN_USD[engine][type] /
 * baseline). Always look multipliers up here — never hardcode a number at a
 * call site.
 *
 * - Digital twins burn the avatar_v $4/min rate on every engine (HeyGen
 *   bills a twin the same on avatar_iv as on avatar_v), expressed via the
 *   grid's `avatar_v.digital_twin` cell rather than hardcoded so it can't
 *   drift from the table. Verified correct for every reachable engine: the
 *   avatar_iii + twin cell is unreachable in practice (twins are HIGH-only
 *   per `tierAvailableForLook`), and the engine-null fallback elsewhere
 *   dispatches avatar_iv, which is also $4 for a twin.
 * - A known, normalized avatar type looks up its exact grid cell.
 * - An unknown/null avatar type uses the per-engine MAX cell so unsynced or
 *   legacy-null rows are never under-billed (iii -> 2.6x, iv -> 4x, v -> 4x).
 */
export function resolveAvatarBurnMultiplier(
  engine: AvatarEngine,
  avatarType?: string | null
): number {
  if (avatarType === 'digital_twin') {
    return toMultiplier(HEYGEN_COST_PER_MIN_USD.avatar_v.digital_twin);
  }

  const normalized = normalizeAvatarTypeForPricing(avatarType);
  if (normalized) {
    return toMultiplier(HEYGEN_COST_PER_MIN_USD[engine][normalized]);
  }

  return TIER_CREDITS_RANGE[ENGINE_TIER[engine]].max;
}

/**
 * Credits/min shown next to a tier in the pickers ("1 min = N credits") and
 * used as the server-side fallback multiplier wherever no fresher
 * resolution is available. `avatarType` AND `pricing` are both REQUIRED (not
 * defaulted) so typecheck proves every call site was swept — pass `null` (or
 * the look's possibly-undefined field) explicitly at look-less call sites to
 * get the safe per-engine max.
 */
export function tierCreditsPerMinute(
  tier: AvatarQualityTier,
  avatarType: string | null | undefined,
  pricing: AvatarPricingMode
): number {
  if (pricing === 'flat') {
    return APPSUMO_AVATAR_CREDITS_PER_MINUTE[tier];
  }
  return resolveAvatarBurnMultiplier(TIER_ENGINE[tier], avatarType);
}

/**
 * The spread of burn rates a tier can produce across HeyGen's avatar types:
 * NORMAL 1–2.6x, STANDARD 3–4x, HIGH 4x. Consumed by `tierCreditsDisplay`
 * and the plan-card copy in `config.ts`.
 */
export function tierCreditsRange(
  tier: AvatarQualityTier,
  pricing: AvatarPricingMode = 'grid'
): {
  min: number;
  max: number;
} {
  if (pricing === 'flat') {
    // Flat pricing has no spread across avatar types.
    const rate = APPSUMO_AVATAR_CREDITS_PER_MINUTE[tier];
    return { min: rate, max: rate };
  }
  return TIER_CREDITS_RANGE[tier];
}

/** Trims float noise off a derived multiplier for display ("2.6", "4"). */
function formatMultiplier(multiplier: number): string {
  return String(Math.round(multiplier * 100) / 100);
}

type TierCreditsDisplay = {
  /**
   * Rate to use for capacity math ("~X min left at this quality", "billed if
   * you confirm"). Exact for a known avatar type; the range MAX when the type
   * isn't known, since a conservative estimate is the right answer for
   * "can I afford this".
   */
  multiplier: number;
  /** "2.6x" for a known avatar type, "1–2.6x" across types when it isn't. */
  label: string;
};

/**
 * What a credit chip/hint should show for a tier. Billing's unknown-type
 * fallback is the per-engine MAX (never under-bill), but leaking that into
 * browse UI would quote 4x for looks that actually cost 1x — so with no
 * selected look, or a look whose `avatarType` hasn't synced, the label shows
 * the tier's range across types instead of a single (wrong) number.
 */
export function tierCreditsDisplay(
  tier: AvatarQualityTier,
  avatarType: string | null | undefined,
  pricing: AvatarPricingMode = 'grid'
): TierCreditsDisplay {
  // Flat pricing is the same number for every avatar type, so it is always
  // an exact rate — never a range.
  if (pricing === 'flat') {
    const multiplier = APPSUMO_AVATAR_CREDITS_PER_MINUTE[tier];
    return { multiplier, label: `${formatMultiplier(multiplier)}x` };
  }
  if (normalizeAvatarTypeForPricing(avatarType)) {
    const multiplier = tierCreditsPerMinute(tier, avatarType, 'grid');
    return { multiplier, label: `${formatMultiplier(multiplier)}x` };
  }
  const { min, max } = TIER_CREDITS_RANGE[tier];
  return {
    multiplier: max,
    label:
      min === max
        ? `${formatMultiplier(max)}x`
        : `${formatMultiplier(min)}–${formatMultiplier(max)}x`
  };
}

/**
 * Single rounding point used by ALL avatar-minute ledger writes (job
 * snapshot at completion, heygenUsageLog, burnAvatarMinutes). Keeping this
 * in one place guarantees the subscription ledger, the credit-pack ledger,
 * and the usage log always agree on the exact same burned-minutes figure.
 */
export function roundBurnedMinutes(
  clipMinutes: number,
  multiplier: number
): number {
  return Math.round(clipMinutes * multiplier * 10_000) / 10_000;
}
